import React, { forwardRef, useCallback, useEffect, useRef } from 'react';
import { useDrag, useDragLayer, useDrop } from 'react-dnd';
import { MenuIcon } from '@heroicons/react/outline';
import Button from './Button';
import { cn } from './cn';

/** Uses the application's existing DndProvider; payloads and moves belong to callers. */
export function DragHandle({
  type,
  item,
  isDraggable = true,
  onIsDragging,
  label = 'Drag to reorder',
}) {
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type,
      item: () => {
        onIsDragging?.(true);
        return item;
      },
      end: () => onIsDragging?.(false),
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
      canDrag: () => isDraggable,
    }),
    [type, item, isDraggable, onIsDragging]
  );
  return (
    <Button
      ref={drag}
      variant="ghost"
      size="sm"
      iconOnly
      disabled={!isDraggable}
      aria-label={label}
      className={cn('cursor-grab', isDragging && 'opacity-50')}
    >
      <MenuIcon className="h-4 w-4" />
    </Button>
  );
}

export const DropTarget = forwardRef(function DropTarget({ accept, canDrop: acceptsItem, onDrop, onIsOver, onHover, variant = 'surface', insertionEdge = 'top', compact = false, showAvailable = false, label, children, className }, forwardedRef) {
  // The HTML5 backend publishes after capturing the source. Expanding zones
  // before that moves the grip during dragstart and can cancel the native drag.
  const isDragPublished = useDragLayer((monitor) => monitor.isSourcePublic());
  const overCallback = useRef(onIsOver);
  overCallback.current = onIsOver;
  const [{ canDrop, isOver, handlerId }, drop] = useDrop({
    accept,
    canDrop: (item, monitor) => acceptsItem ? acceptsItem(item, monitor) : true,
    drop: (item, monitor) => {
      if (!monitor.didDrop()) onDrop?.(item);
    },
    hover: (item, monitor) => {
      if (monitor.canDrop() && monitor.isOver({ shallow: true })) onHover?.(item, monitor);
    },
    collect: (monitor) => ({ canDrop: monitor.canDrop(), isOver: monitor.isOver({ shallow: true }), handlerId: monitor.getHandlerId() }),
  });
  useEffect(() => {
    overCallback.current?.(canDrop && isOver);
  }, [canDrop, isOver]);
  const connectDrop = useCallback(node => {
    drop(node);
    if (typeof forwardedRef === 'function') forwardedRef(node);
    else if (forwardedRef) forwardedRef.current = node;
  }, [drop, forwardedRef]);
  const showZone = canDrop && isDragPublished;
  return (
    <div
      ref={connectDrop}
      data-handler-id={handlerId}
      className={cn(
        'relative min-w-0',
        variant === 'group' && 'rounded-bun transition-all',
        variant === 'group' && (compact ? 'min-h-[32px]' : 'min-h-[48px]'),
        variant === 'group' && showAvailable && showZone && !isOver && 'border-2 border-dashed border-bun-line',
        variant === 'zone' && (showZone
          ? 'mx-[18px] my-2 flex min-h-[44px] items-center justify-center rounded-bun-lg border border-dashed border-bun-blue bg-bun-wash px-3 py-2 text-center text-bun-meta text-bun-blue'
          : 'h-4'),
        showZone && isOver && variant !== 'insert' && 'bg-bun-wash outline outline-2 outline-bun-blue outline-offset-[-2px]',
        showZone && isOver && variant === 'insert' && insertionEdge && 'before:pointer-events-none before:absolute before:inset-x-0 before:z-10 before:h-[3px] before:rounded-full before:bg-bun-blue',
        showZone && isOver && variant === 'insert' && insertionEdge === 'top' && 'before:top-0',
        showZone && isOver && variant === 'insert' && insertionEdge === 'bottom' && 'before:bottom-0',
        className
      )}
    >
      {variant === 'zone' ? (showZone ? label : null) : children}
    </div>
  );
});
